import { useRef, useEffect } from 'react';

const points = [
  {
    icon: 'ri-brain-line',
    title: '心の仕組みをわかりやすく説明します',
    text: '難しい言葉ではなく、あなたに合ったたとえ話や図を使いながら、「なぜそうなるのか」を一緒に見ていきます。',
  },
  {
    icon: 'ri-heart-line',
    title: '解決策を与えるセッションではありません',
    text: '「こうすれば良い」を提供するのではなく、あなた自身の中にある答えを一緒に探す時間です。',
  },
  {
    icon: 'ri-time-line',
    title: 'たった一回でも、何かが変わることがあります',
    text: '「仕組みがわかった瞬間」が入口になります。長期プログラムへの誘導は行いません。',
  },
];

const examples = [
  '心が軽くなる',
  '安心できる',
  '次の一歩をどこに踏み出したらいいか見えてくる',
  '自分の中から気づきが出てきて嬉しい',
  '緊張がほどけ心身ともにゆったりした感じを感じる',
  '私ってこういう感じだったんだと自分自身への感覚が変わる',
];

export default function SessionInfoSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="w-full py-24 px-4" style={{ backgroundColor: '#FAF8F5' }}>
      <div ref={ref} className="max-w-2xl mx-auto opacity-0 translate-y-8 transition-all duration-1000">
        <p className="text-center font-sans text-[#8FA68B] text-xs tracking-[0.4em] mb-6 uppercase">session</p>
        <h2 className="font-serif text-3xl md:text-4xl text-[#3A3530] text-center leading-snug mb-16">
          セッション情報
        </h2>

        {/* Main card */}
        <div
          className="rounded-lg p-8 md:p-10 mb-10"
          style={{ backgroundColor: 'white', border: '1px solid #EDE7DC' }}
        >
          <p className="font-serif text-[#3A3530] text-lg mb-6 text-center">
            考えすぎてしまう人のための、自分に戻る体験セッション
          </p>

          {/* Session details */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { label: '時間', value: '60分' },
              { label: '料金', value: '¥4,000' },
              { label: '定員', value: '月3〜4名' },
            ].map((item) => (
              <div
                key={item.label}
                className="text-center py-5 rounded-lg"
                style={{ backgroundColor: 'rgba(143, 166, 139, 0.08)', border: '1px solid rgba(143, 166, 139, 0.2)' }}
              >
                <p className="font-sans text-[#8FA68B] text-xs tracking-widest mb-2">{item.label}</p>
                <p className="font-serif text-[#3A3530] text-base">{item.value}</p>
                {item.label === '料金' && (
                  <p className="font-sans text-[#8B8480] text-xs mt-1">税込</p>
                )}
              </div>
            ))}
          </div>

          {/* Session flow */}
          <div className="mb-8 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(143, 166, 139, 0.25)' }}>
            <div className="px-5 py-3" style={{ backgroundColor: 'rgba(143, 166, 139, 0.12)' }}>
              <p className="font-sans text-[#8FA68B] text-xs tracking-widest">セッションの流れ</p>
            </div>
            <div className="divide-y" style={{ borderColor: 'rgba(143, 166, 139, 0.15)' }}>
              <div className="flex items-start gap-4 px-5 py-5">
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center">
                  <div className="text-center">
                    <p className="font-sans text-[#C2A882] text-[10px] tracking-widest leading-none mb-1">前半</p>
                    <div className="w-6 h-px mx-auto" style={{ backgroundColor: 'rgba(194, 168, 130, 0.5)' }} />
                  </div>
                </div>
                <p className="font-sans text-[#5A5450] text-xs leading-relaxed pt-1">
                  あなたの「今感じてること」をそのままお話ください。
                </p>
              </div>
              <div className="flex items-start gap-4 px-5 py-5">
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center">
                  <div className="text-center">
                    <p className="font-sans text-[#8FA68B] text-[10px] tracking-widest leading-none mb-1">後半</p>
                    <div className="w-6 h-px mx-auto" style={{ backgroundColor: 'rgba(143, 166, 139, 0.5)' }} />
                  </div>
                </div>
                <p className="font-sans text-[#5A5450] text-xs leading-relaxed pt-1">
                  お伺いしたお話を心の仕組みでひも解いていきながら、あなたの状態に合わせた個別カウンセリングを行っていきます。
                </p>
              </div>
            </div>
          </div>

          {/* 3 key points */}
          <div className="space-y-5 mb-8">
            {points.map((p, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full" style={{ backgroundColor: 'rgba(143, 166, 139, 0.15)' }}>
                  <i className={`${p.icon} text-[#8FA68B] text-sm`} />
                </div>
                <div>
                  <p className="font-serif text-[#3A3530] text-sm mb-1">{p.title}</p>
                  <p className="font-sans text-[#6B6460] text-xs leading-relaxed">{p.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Examples */}
          <div className="rounded-lg p-6" style={{ backgroundColor: 'rgba(194, 168, 130, 0.08)', border: '1px solid rgba(194, 168, 130, 0.25)' }}>
            <p className="font-sans text-[#C2A882] text-xs tracking-widest mb-4">たとえばこんなことが起きます</p>
            <ul className="space-y-3">
              {examples.map((ex, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#C2A882] mt-1 flex-shrink-0 text-xs">・</span>
                  <p className="font-sans text-[#6B6460] text-xs leading-relaxed">{ex}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Client voice quotes */}
          <div className="mt-6 space-y-4">
            {[
              'ずっとAかBかで悩んでいたけど、話しているうちに、本当は"どちらか"じゃなくて、"決めるのが怖かった"んだと気づいた',
              '"自分が悪い"と思っていたことが、心の仕組みで見ると全然違う意味だった。——それだけで、すごく軽くなった',
            ].map((quote, i) => (
              <div
                key={i}
                className="rounded-lg px-6 py-5"
                style={{ backgroundColor: 'rgba(143, 166, 139, 0.06)', border: '1px solid rgba(143, 166, 139, 0.2)' }}
              >
                <p className="font-serif text-[#3A3530] text-sm leading-loose">
                  &ldquo;{quote}&rdquo;
                </p>
              </div>
            ))}
          </div>

          {/* Orange disclaimer box */}
          <div
            className="mt-6 rounded-lg px-6 py-5 text-center"
            style={{ backgroundColor: '#FFF7F0', border: '1.5px solid rgba(230, 140, 70, 0.45)' }}
          >
            <p className="font-sans text-[#8B5E3C] text-xs leading-relaxed tracking-wide">
              セッション後に継続を勧めることはありません。<br />
              あなたのタイミングで、いつでもどうぞ。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
